/**
 * @fileoverview A campaign's overbooking settings, on the campaign edit page.
 *
 * Arthur's rule from the 3 November call: overbooking is a property of the
 * campaign. Ops set the standby share of each shift here, and whether a BA
 * booking a standby seat is told so, with the chance of working the notice
 * shows. Saving applies the share to every shift of the campaign's plans.
 *
 * Kept out of the main campaign form on purpose: it saves on its own, through
 * its own mutation, so it can never hold up or break a campaign save.
 */
import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { readGraphQLData } from 'Components/PowerBanks/hooks'
import SubmitButton from 'Components/Form/SubmitButton'
import { SnackBarContext } from 'Components/SnackBar'
import {
  fetchCampaignStandbySettings,
  updateCampaignStandbySettings,
} from 'Module/graphQL'

/** What the notice says until ops pick their own number. */
const DEFAULT_WORK_CHANCE = 96

/** Two standby BAs per van of ten. */
const DEFAULT_PERCENT = 20

const Wrapper = styled.section`
  margin: 24px 0;
  padding: 20px;
  border: 1px solid #eceef0;
  border-radius: 4px;
  background: #ffffff;
  max-width: 720px;

  h3 {
    margin: 0 0 4px;
    font-size: 18px;
  }
  .intro {
    color: #5a6b82;
    margin: 0 0 16px;
  }
  .row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;
  }
  .row label {
    min-width: 260px;
    font-weight: 600;
  }
  .row input[type='number'] {
    width: 72px;
    padding: 4px 8px;
    border: 1px solid #d9dee6;
    border-radius: 4px;
  }
  .hint {
    color: #5a6b82;
    font-size: 13px;
  }
  .preview {
    border-left: 4px solid #f2a33a;
    background: #fff7ea;
    border-radius: 4px;
    padding: 10px 12px;
    margin: 4px 0 16px;
    max-width: 420px;
  }
  .preview strong {
    display: block;
  }
`

const CampaignStandbySettings = ({ campaignId }) => {
  const runSnackBar = useContext(SnackBarContext)
  const [loaded, setLoaded] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [percent, setPercent] = useState(DEFAULT_PERCENT)
  const [noticeEnabled, setNoticeEnabled] = useState(false)
  const [workChance, setWorkChance] = useState(DEFAULT_WORK_CHANCE)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!campaignId) return
    let cancelled = false
    const load = async () => {
      try {
        const data = await readGraphQLData(
          await fetchCampaignStandbySettings({ campaignId }),
        )
        const settings = data.campaignStandbySettings
        if (cancelled || !settings) return
        const isOn =
          settings.standbyPercent !== null &&
          settings.standbyPercent !== undefined
        setEnabled(isOn)
        setPercent(isOn ? settings.standbyPercent : DEFAULT_PERCENT)
        setNoticeEnabled(!!settings.standbyNoticeEnabled)
        setWorkChance(
          settings.standbyWorkChance === null ||
            settings.standbyWorkChance === undefined
            ? DEFAULT_WORK_CHANCE
            : settings.standbyWorkChance,
        )
      } catch (error) {
        runSnackBar({
          type: 'error',
          msg: 'Could not load the overbooking settings.',
        })
      } finally {
        if (!cancelled) setLoaded(true)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [campaignId]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = async () => {
    setSaving(true)
    try {
      const data = await readGraphQLData(
        await updateCampaignStandbySettings({
          campaignId,
          standbyPercent: enabled ? Number(percent) : null,
          standbyNoticeEnabled: noticeEnabled,
          standbyWorkChance: Number(workChance),
        }),
      )
      const result = data.updateCampaignStandbySettings
      runSnackBar({
        type: 'success',
        msg: enabled
          ? `Saved. ${result.shifts} shifts now carry ${Number(
              percent,
            )}% standby: added ${result.created}, removed ${result.removed}.`
          : `Saved. Overbooking is off, ${
              result.removed
            } unbooked standby seats removed.`,
      })
    } catch (error) {
      runSnackBar({
        type: 'error',
        msg: (error && error.message) || 'Could not save the settings.',
      })
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null

  return (
    <Wrapper>
      <h3>Overbooking</h3>
      <p className="intro">
        Standby BAs on top of every shift, so a no show is covered at the
        meeting point.
      </p>

      <div className="row">
        <label htmlFor="standby-enabled">Overbook this campaign</label>
        <input
          id="standby-enabled"
          type="checkbox"
          checked={enabled}
          onChange={event => setEnabled(event.target.checked)}
        />
      </div>

      {enabled && (
        <>
          <div className="row">
            <label htmlFor="standby-campaign-percent">
              Standby BAs per shift
            </label>
            <input
              id="standby-campaign-percent"
              type="number"
              min="0"
              max="100"
              step="5"
              value={percent}
              onChange={event => setPercent(event.target.value)}
            />
            <span>%</span>
            <span className="hint">
              20% gives a van of ten two standby BAs, two vans four.
            </span>
          </div>

          <div className="row">
            <label htmlFor="standby-notice">
              Tell BAs a seat is a standby seat
            </label>
            <input
              id="standby-notice"
              type="checkbox"
              checked={noticeEnabled}
              onChange={event => setNoticeEnabled(event.target.checked)}
            />
            <span className="hint">
              Off, a standby seat looks like any other mission in the app.
            </span>
          </div>

          {noticeEnabled && (
            <>
              <div className="row">
                <label htmlFor="standby-chance">Chance of working shown</label>
                <input
                  id="standby-chance"
                  type="number"
                  min="0"
                  max="100"
                  value={workChance}
                  onChange={event => setWorkChance(event.target.value)}
                />
                <span>%</span>
              </div>
              <div className="preview">
                <strong>Standby shift</strong>
                You have a {Number(workChance) || 0}% chance of working this
                shift.
              </div>
            </>
          )}
        </>
      )}

      <SubmitButton
        value="Save overbooking"
        size="small"
        disabled={saving}
        onClick={onSave}
      />
    </Wrapper>
  )
}

CampaignStandbySettings.propTypes = {
  campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
}

export default CampaignStandbySettings
